<template>
  <v-dialog v-model="dialog" max-width="600px">
    <v-card>
      <v-card-title>
        <span class="text-h5">Create New Project</span>
      </v-card-title>

      <v-card-text>
        <v-form ref="form" v-model="valid">
          <v-container>
            <v-row>
              <v-col cols="12" md="8">
                <v-text-field
                  v-model="project.name"
                  label="Project Name"
                  required
                  :rules="[v => !!v || 'Name is required']"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="project.acronym"
                  label="Acronym"
                  hint="2-3 characters"
                  persistent-hint
                  :rules="[
                    v => !v || (v.length >= 2 && v.length <= 3) || 'Must be 2-3 characters'
                  ]"
                  @update:modelValue="onAcronymInput"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="project.description"
                  label="Description"
                  rows="3"
                ></v-textarea>
              </v-col>
              
              <v-col cols="12">
                <v-select
                  v-model="project.source_project_id"
                  :items="projects"
                  item-title="name"
                  item-value="id"
                  label="Copy configuration from (optional)"
                  clearable
                  hint="Select a project to copy settings from"
                  persistent-hint
                ></v-select>
              </v-col>

              <v-col cols="12" v-if="project.source_project_id">
                <div class="text-subtitle-2 mb-2">What to copy?</div>
                <div class="d-flex flex-wrap gap-4">
                  <v-checkbox
                    v-model="project.copy_levels"
                    label="Maturity & Risk Levels"
                    density="compact"
                    hide-details
                  ></v-checkbox>
                  <v-checkbox
                    v-model="project.copy_categories"
                    label="Categories"
                    density="compact"
                    hide-details
                  ></v-checkbox>
                  <v-checkbox
                    v-model="project.copy_requirements"
                    label="Requirements"
                    density="compact"
                    hide-details
                  ></v-checkbox>
                  <v-checkbox
                    v-model="project.copy_youtrack_config"
                    label="YouTrack Config"
                    density="compact"
                    hide-details
                  ></v-checkbox>
                </div>
              </v-col>
            </v-row>
          </v-container>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="blue-darken-1" variant="text" @click="close">
          Cancel
        </v-btn>
        <v-btn 
          color="blue-darken-1" 
          variant="text" 
          @click="save" 
          :loading="loading"
          :disabled="!valid"
        >
          Create
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'CreateProjectDialog',
  props: {
    modelValue: Boolean
  },
  emits: ['update:modelValue', 'created'],
  data() {
    return {
      valid: false,
      loading: false,
      project: {
        name: '',
        acronym: '',
        description: '',
        source_project_id: null,
        copy_levels: true,
        copy_categories: true,
        copy_requirements: true,
        copy_youtrack_config: true
      }
    }
  },
  computed: {
    ...mapGetters('projects', ['projects']),
    dialog: {
      get() {
        return this.modelValue
      },
      set(value) {
        this.$emit('update:modelValue', value)
      }
    }
  },
  methods: {
    ...mapActions('projects', ['createProject', 'selectProject']),
    onAcronymInput(val) {
      const str = val != null ? String(val) : ''
      this.project.acronym = str ? str.toUpperCase() : str
    },
    close() {
      this.dialog = false
      this.resetForm()
    },
    resetForm() {
      this.project = {
        name: '',
        acronym: '',
        description: '',
        source_project_id: null,
        copy_levels: true,
        copy_categories: true,
        copy_requirements: true,
        copy_youtrack_config: true
      }
    },
    async save() {
      if (!this.$refs.form.validate()) return

      this.loading = true
      try {
        const payload = {
          name: this.project.name,
          acronym: this.project.acronym,
          description: this.project.description,
          source_project_id: this.project.source_project_id,
          copy_levels: this.project.source_project_id ? this.project.copy_levels : false,
          copy_categories: this.project.source_project_id ? this.project.copy_categories : false,
          copy_requirements: this.project.source_project_id ? this.project.copy_requirements : false,
          copy_youtrack_config: this.project.source_project_id ? this.project.copy_youtrack_config : false
        }

        const newProject = await this.createProject(payload)
        
        const acronym = newProject.acronym || 'def'
        await this.$router.push(`/${acronym.toLowerCase()}/dashboard`)
        
        this.$emit('created', newProject)
        this.close()
      } catch (error) {
        console.error('Failed to create project:', error)
        // Show error notification (if global notification system exists)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

